import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { CheckCircle2, Loader2, ShieldCheck } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { StepSection } from '@/components/step-section';
import {
  PayoutFields,
  EMPTY_PAYOUT_FORM,
  describe_payout_view,
  payout_payload,
  validate_payout,
  type PayoutForm,
} from '@/components/payout-fields';
import { ApiError } from '@/lib/api';
import { field_errors_from, form_error_message } from '@/lib/forms';
import { use_draft, use_save_payout, use_saved_payout } from '@/features/onboarding/hooks';

/**
 * The payout step.
 *
 * It did not exist. The API accepted payout details from day one and the schema tracked the step,
 * but no console screen ever asked, so every vendor arrived at submit with none — and submit did
 * not check. The result was approved vendors taking bookings with no destination for the money.
 * Now the step exists and submit requires it.
 */
export function PayoutPage() {
  const navigate = useNavigate();
  const draft_q = use_draft();
  const saved_q = use_saved_payout({ enabled: !!draft_q.data });
  const save_m = use_save_payout();
  const [form, set_form] = useState<PayoutForm>(EMPTY_PAYOUT_FORM);
  const [error, set_error] = useState<string | null>(null);
  const [field_errors, set_field_errors] = useState<Record<string, string>>({});

  useEffect(() => {
    if (!draft_q.isFetched) return;
    if (!draft_q.data) {
      navigate('/onboarding/start', { replace: true });
      return;
    }
    if (!draft_q.data.basic_info_completed) {
      navigate('/onboarding/basic-info', { replace: true });
    }
  }, [draft_q.isFetched, draft_q.data, navigate]);

  /*
   * Prefill the METHOD only.
   *
   * The account number and UPI id are encrypted server-side and never come back, so there is
   * nothing to put in those inputs. Preselecting the method they chose last time is still worth
   * doing — it means a vendor revisiting the step to fix a typo does not also have to re-pick
   * bank vs UPI.
   */
  const saved = saved_q.data ?? null;
  const [method_prefilled, set_method_prefilled] = useState(false);
  useEffect(() => {
    if (method_prefilled || !saved) return;
    set_form((f) => ({ ...f, method: saved.method }));
    set_method_prefilled(true);
  }, [saved, method_prefilled]);

  function update<K extends keyof PayoutForm>(key: K, value: PayoutForm[K]) {
    set_form((f) => ({ ...f, [key]: value }));
    set_field_errors((fe) => {
      if (!(key in fe)) return fe;
      const next = { ...fe };
      delete next[key];
      return next;
    });
  }

  async function handle_submit(e: React.FormEvent) {
    e.preventDefault();
    set_error(null);

    const client_errors = validate_payout(form);
    if (Object.keys(client_errors).length > 0) {
      set_field_errors(client_errors);
      set_error('Please fix the highlighted fields below.');
      return;
    }
    set_field_errors({});

    try {
      await save_m.mutateAsync(payout_payload(form));
      navigate('/onboarding/terms');
    } catch (err) {
      set_field_errors(field_errors_from(err));
      set_error(
        err instanceof ApiError
          ? form_error_message(err, 'Could not save payout details', { highlights_fields: true })
          : 'Could not save payout details',
      );
    }
  }

  return (
    <form onSubmit={handle_submit} noValidate>
      <StepSection
        step_key="payout"
        title="Payout details"
        description="Where your earnings are sent after each completed booking or order. Required to submit your application."
        error={error}
        footer={
          <Button type="submit" variant="gradient" disabled={save_m.isPending} data-testid="payout-submit">
            {save_m.isPending && <Loader2 className="h-4 w-4 animate-spin" />}
            Save &amp; continue
          </Button>
        }
      >
        {saved && (
          <div
            className="flex items-start gap-2 rounded-md border border-emerald-500/25 bg-emerald-500/10 px-3 py-2 text-sm"
            data-testid="payout-saved-summary"
          >
            <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0 text-emerald-600" />
            <span>
              Already saved: <strong>{describe_payout_view(saved)}</strong>. Re-enter the details below only
              if you want to change them.
            </span>
          </div>
        )}

        <PayoutFields form={form} field_errors={field_errors} on_change={update} />

        <div className="flex items-start gap-2 rounded-md border border-border bg-muted/40 px-3 py-2 text-xs text-muted-foreground">
          <ShieldCheck className="mt-0.5 h-4 w-4 shrink-0 text-primary" />
          {/* Method-aware: talking about "the last four digits" of a UPI ID makes no sense. */}
          <span>
            {form.method === 'UPI'
              ? 'Your UPI ID is encrypted before it is stored and is only ever shown back masked — not to you, not to our staff.'
              : 'Your account number is encrypted before it is stored and is never shown again — not to you, not to our staff. Only the last four digits stay readable so you can recognise the account.'}{' '}
            Once you are approved these details stay editable, but only once every 30 days.
          </span>
        </div>
      </StepSection>
    </form>
  );
}
